// Day-range filtering shared by the Issues, Checklist, Schedule and
// Review Center lists.
//
// === Why two day keys and not one ===
//
// The two kinds of date on these rows are stored on different scales,
// and one shared helper is exactly how that difference gets lost:
//
//   INSTANTS  - createdAt, updatedAt, completedAt, a commit's ts. A real
//               moment. Which DAY it falls on depends on who is looking:
//               an issue raised 20:00 in New York is 00:00 UTC the next
//               day, so the person who just raised it must still see it
//               under today. Read with local accessors.
//
//   DATE-ONLY - dueDate, startDate, endDate. The picker stores midnight
//               UTC of the day chosen; every one of these in the
//               database is exactly T00:00:00Z (verified against
//               staging). Read with UTC accessors, or everyone west of
//               UTC sees due dates land a day early.
//
// Same split the issue list route already makes server-side - see the
// "Today, in the reader's day" block in back/routes/issue.js.
//
// Keys are 'YYYY-MM-DD' strings, which the native <input type="date">
// also emits, so range tests are plain lexicographic string compares -
// no timezone left to get wrong at the comparison step.

const pad = (n) => String(n).padStart(2, '0');

// A real instant -> the reader's calendar day.
export const localDayKey = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(+d)) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// A date-only field (midnight UTC) -> the day it names.
export const utcDayKey = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(+d)) return '';
  return d.toISOString().slice(0, 10);
};

// Is this day key inside [from, to]? Either end may be blank (open).
//
// A row with no date is OUT whenever a range is set, never in: asking
// for "due between the 1st and the 7th" is a question about dated work,
// and an undated row is not a quiet member of every window. Same rule
// the Overdue filter already applies with { dueDate: { $ne: null } }.
export const inDayRange = (key, from, to) => {
  if (!from && !to) return true;
  if (!key) return false;
  if (from && key < from) return false;
  if (to && key > to) return false;
  return true;
};

// True when neither end of the range is set.
export const rangeEmpty = (r) => !r || (!r.from && !r.to);

// A range read off the URL, for deep links from dashboards:
//   ?createdFrom=2026-08-01&createdTo=2026-08-31
export const rangeFromParams = (params, prefix) => ({
  from: params.get(`${prefix}From`) || '',
  to: params.get(`${prefix}To`) || '',
});

// Human summary for a filter chip / tooltip: "1 Aug - 31 Aug", "from 1
// Aug", "until 31 Aug".
export const describeRange = (r) => {
  if (rangeEmpty(r)) return '';
  const nice = (k) => {
    const [y, m, d] = String(k).split('-').map(Number);
    if (!y || !m || !d) return k;
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', timeZone: 'UTC',
    });
  };
  if (r.from && r.to) return `${nice(r.from)} - ${nice(r.to)}`;
  return r.from ? `from ${nice(r.from)}` : `until ${nice(r.to)}`;
};
